/**
 * AI:
 * Status: 'in progress'
 * Type: Component
 * SubType: CrudIntegration
 * Reason: Dashboard component for JbJobType entity displaying stats cards grouped by executionStatus and a QL widget table with create support
 */
import {MvsCoreService} from "../../../../../core/shared/service/mvs-core.service";
import {MvsCrudService} from "../../../../../core/shared/service/crud/mvs-crud.service";
import {Component, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {WidgetData} from '../../../../../core/shared/widget/widget-data';
import {WidgetFactory} from '../../../../../core/shared/widget/service/widget.factory';
import {ObjectRequestListGroupBy} from '../../../../../core/shared/dto/object/request/object-request-list-group-by';
import {ObjectRequestListAttribute} from '../../../../../core/shared/dto/object/request/object-request-list-attribute';
import {
    DtoListAttributeRequestAggregateEnum
} from '../../../../../core/shared/dto/enum/dto-list-attribute-request-aggregate.enum';
import {Sorting} from '../../../../../core/shared/misc/sorting';
import {ObjectIdentifierData} from '../../../../../core/shared/basic/object-identifier-data';
import {ObjectIdentifier} from '../../../../../core/shared/basic/object-identifier';
import {MvsObjectNavigationService} from '../../../../../core/shared/navigation/mvs-object-navigation.service';
import {MvsObjectNavigationEntry} from '../../../../../core/shared/navigation/mvs-object-navigation-entry';
import {MvsObjectNavigationActionEnum} from '../../../../../core/shared/navigation/mvs-object-navigation-action-enum';
import {FilterCriteria} from '../../../../../core/shared/filter/api/filter.criteria';
import {JobTypeStat} from '../../../interface/job-type-stat.interface';
import {JbJobRunService} from '../../../service/api/jb-job-run.service';
import {ConfirmationService} from 'primeng/api';

@Component({
    selector: 'jb-job-type-dashboard',
    templateUrl: 'jb-job-type-dashboard.component.html',
    standalone: false
})
export class JbJobTypeDashboardComponent implements OnInit, OnChanges, OnDestroy {

    jobTypeService: MvsCrudService;
    busy: boolean;
    initialized: boolean;

    stats: JobTypeStat[] = [];
    jobTypeTableWidget: WidgetData;

    selectedJobType: any = null;
    selectedJobTypeId: number = null;
    actionBusy: boolean = false;

    constructor(
        protected coreService: MvsCoreService,
        protected navigationService: MvsObjectNavigationService,
        protected jobRunService: JbJobRunService,
        protected confirmationService: ConfirmationService
    ) {
        this.jobTypeService = this.coreService.getCrudService("jb.JbJobType");
    }

    ngOnInit(): void {
        this.initComponent();
    }

    /**
     * Initialize Component.
     */
    initComponent(): void {
        this.refreshComponent();
    }

    /**
     * Refresh Component.
     */
    refreshComponent(): void {
        this.refreshStats();
        this.refreshJobTypeTableWidget();
        this.initialized = true;
    }

    /**
     * Refresh stats by querying JbJobType grouped by executionStatus.
     */
    refreshStats(): void {
        const activeFilter = FilterCriteria.create('entityStatus', FilterCriteria.cOperatorEqual, 3);

        this.jobTypeService.groupBy(
            ObjectRequestListGroupBy.create(
                false,
                [activeFilter],
                [],
                ['executionStatus'],
                [new ObjectRequestListAttribute('executionStatus', 'Count', DtoListAttributeRequestAggregateEnum.count)]
            )
        ).subscribe(result => {
            let totalActive = 0;
            let running = 0;
            let idle = 0;
            let disabled = 0;

            if (result && result.entries) {
                for (const entry of result.entries) {
                    const count = entry['executionStatus_count'] || 0;
                    const status = entry['executionStatus'];
                    totalActive += count;

                    if (status === 1) { // RUNNING (ordinal 1)
                        running = count;
                    } else if (status === 0) { // IDLE (ordinal 0)
                        idle = count;
                    } else if (status === 2) { // DISABLED (ordinal 2)
                        disabled = count;
                    }
                }
            }

            this.stats = [
                {
                    label: 'Gesamt Aktiv',
                    value: totalActive,
                    icon: 'fa-regular fa-layer-group',
                    colorClass: 'text-primary'
                },
                {
                    label: 'Laufend',
                    value: running,
                    icon: 'fa-regular fa-play',
                    colorClass: 'text-cyan-500'
                },
                {
                    label: 'Bereit',
                    value: idle,
                    icon: 'fa-regular fa-pause',
                    colorClass: 'text-green-500'
                },
                {
                    label: 'Deaktiviert',
                    value: disabled,
                    icon: 'fa-regular fa-ban',
                    colorClass: 'text-red-500'
                }
            ];
        });
    }

    /**
     * Refresh the main JbJobType table widget with FK resolution and name ASC sorting.
     */
    refreshJobTypeTableWidget(): void {
        /**
         * AI:
         * Status: 'in progress'
         * Type: Widget
         * SubType: Runtime
         * Reason: JbJobType table widget with FK resolution for browsing all job types sorted by name
         */
        this.jobTypeTableWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'jb.dashboard.jobType.table.main',
            'Job-Typen',
            'jb.JbJobType',
            [],
            [new Sorting('e.name', true)]
        );
    }

    /**
     * Handle row click to open JbJobType in the side navigation panel.
     */
    handleObjectSelect(object: ObjectIdentifierData | ObjectIdentifier): void {
        // Store selection for action panel
        this.selectedJobTypeId = object.objectId as number;

        // Load the full object to show name in action panel
        this.jobTypeService.get(this.selectedJobTypeId).subscribe(result => {
            this.selectedJobType = result;
        });

        // Navigate to side panel (existing behavior)
        const navigationEntry = MvsObjectNavigationEntry.createNavigationEntry(
            object.objectType,
            object.objectId,
            'object',
            null,
            null,
            null,
            MvsObjectNavigationActionEnum.any,
            'side'
        );
        this.navigationService.navigateTo(navigationEntry, 'right');
    }

    /**
     * Trigger ad-hoc execution of the selected job.
     */
    onRunNow(): void {
        if (!this.selectedJobTypeId || !this.selectedJobType) return;

        this.confirmationService.confirm({
            message: `Job '${this.selectedJobType.name}' jetzt ausführen?`,
            header: 'Job starten',
            icon: 'fa-regular fa-play',
            acceptLabel: 'Ja, starten',
            rejectLabel: 'Abbrechen',
            accept: () => {
                this.actionBusy = true;
                this.jobRunService.runJob(this.selectedJobTypeId).subscribe({
                    next: () => {
                        this.actionBusy = false;
                        setTimeout(() => this.refreshComponent(), 3000);
                    },
                    error: () => {
                        this.actionBusy = false;
                    }
                });
            }
        });
    }

    /**
     * Toggle job between IDLE and DISABLED.
     * Loads a fresh entity before PUT to prevent optimistic lock exceptions
     * caused by concurrent scheduler modifications to the version field.
     */
    toggleJobStatus(): void {
        if (!this.selectedJobTypeId) return;

        this.actionBusy = true;

        // Fresh GET to avoid stale version field (scheduler may have modified entity)
        this.jobTypeService.get(this.selectedJobTypeId).subscribe({
            next: (freshEntity: any) => {
                const currentStatus = freshEntity.executionStatus;
                // IDLE=0, RUNNING=1, DISABLED=2
                const newStatus = currentStatus === 2 ? 0 : 2;
                const updatedDto = { ...freshEntity, executionStatus: newStatus };

                this.jobTypeService.update(updatedDto).subscribe({
                    next: (result) => {
                        this.selectedJobType = result;
                        this.actionBusy = false;
                        this.refreshStats();
                    },
                    error: () => {
                        this.actionBusy = false;
                    }
                });
            },
            error: () => {
                this.actionBusy = false;
            }
        });
    }

    /**
     * Clear the selected job type.
     */
    clearSelection(): void {
        this.selectedJobType = null;
        this.selectedJobTypeId = null;
    }

    /**
     * Get human-readable execution status label.
     */
    getExecutionStatusLabel(ordinal: number): string {
        switch (ordinal) {
            case 0: return 'Bereit';
            case 1: return 'Läuft';
            case 2: return 'Deaktiviert';
            default: return '-';
        }
    }

    /**
     * Process changes within Binding.
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }

        this.refreshComponent();
    }

    /**
     * Destroy component.
     */
    ngOnDestroy(): void {
    }
}
